import {
  replaceAccessGroupRules,
  type AccessGroup,
  type AccessRule,
  type RuleInput,
} from '@/api/core/access-groups';
import {
  inviteGroupMember,
  resendGroupMember,
  revokeAccessMember,
  updateAccessMember,
  type AccessMember,
} from '@/api/core/access-members';
import { canReadDomain } from '@/api/core/authorization';
import { isApiError } from '@/api/http/errors';
import { isUuid } from '@/lib/validation';
import { Button, Input } from '@mgrvtechgroup/admin-ds';
import { useRef, useState } from 'react';
import type { PropertyRuntime } from '../property/property-queries';
import { ClueDevicePicker } from './clue-device-picker';

const days = [
  ['MON', '월요일'],
  ['TUE', '화요일'],
  ['WED', '수요일'],
  ['THU', '목요일'],
  ['FRI', '금요일'],
  ['SAT', '토요일'],
  ['SUN', '일요일'],
] as const;
function kst(value: string): string | null {
  if (!/^\d{4}-\d\d-\d\dT\d\d:\d\d$/.test(value)) return null;
  const d = new Date(`${value}:00+09:00`);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}
/** 그룹 규칙 교체와 관리자 초대는 현재 revision과 권한을 확인한다. */
export function GroupAccessManagement({
  runtime,
  group,
  rules,
  members,
  generation,
  reload,
}: {
  runtime: PropertyRuntime;
  group: AccessGroup;
  rules: AccessRule[];
  members: AccessMember[];
  generation: number;
  reload: () => Promise<unknown>;
}) {
  const [draft, setDraft] = useState<RuleInput[]>(() =>
    rules.map(({ id, device_id, floor, schedule }) => ({ id, device_id, floor, schedule })),
  );
  const [device, setDevice] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [floor, setFloor] = useState('');
  const [schedule, setSchedule] = useState<Record<string, string>>({});
  const [user, setUser] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [editing, setEditing] = useState<AccessMember>();
  const [confirm, setConfirm] = useState<AccessMember>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const inviteAttempt = useRef<{ key: string; commandId: string; id: string } | null>(null);
  const authorized = () => {
    const s = runtime.session.getSnapshot();
    return (
      s.generation === generation &&
      s.status === 'authorized' &&
      !!s.authorization &&
      !s.deniedDomains.includes('entitlement') &&
      canReadDomain(s.authorization, 'entitlement', 4)
    );
  };
  const execute = async (action: () => Promise<unknown>, success: string) => {
    if (busy || !authorized() || group.origin !== 'CORE') return;
    setBusy(true);
    setMessage('');
    try {
      const result = await action();
      if (!authorized()) return;
      setMessage(
        result &&
          typeof result === 'object' &&
          'message_sent' in result &&
          result.message_sent === false
          ? '초대는 저장됐지만 알림 발송에 실패했습니다.'
          : success,
      );
      setPhone('');
      setConfirm(undefined);
      setEditing(undefined);
      await reload();
    } catch (error) {
      if (!authorized()) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('entitlement', generation);
      setMessage(
        isApiError(error) && error.status === 409
          ? '변경 사항이 충돌했습니다. 다시 조회해 주세요.'
          : '요청을 처리하지 못했습니다.',
      );
    } finally {
      setBusy(false);
    }
  };
  const addRule = () => {
    const entries = Object.entries(schedule)
      .filter(([, v]) => v.trim())
      .map(([day, v]) => [day, v.split(',').map((x) => x.trim())] as const);
    const number = floor === '' ? null : Number(floor);
    if (
      !isUuid(device) ||
      (number !== null && (!Number.isSafeInteger(number) || floor.trim() === '')) ||
      entries.some(([, v]) =>
        v.some((x) => !/^(?:[01]\d|2[0-3]):[0-5]\d-(?:(?:[01]\d|2[0-3]):[0-5]\d|24:00)$/.test(x)),
      ) ||
      draft.length >= 500
    ) {
      setMessage('기기, 층 또는 시간표를 확인해 주세요.');
      return;
    }
    setDraft([
      ...draft,
      {
        id: crypto.randomUUID(),
        device_id: device,
        floor: number,
        schedule: Object.fromEntries(entries),
      },
    ]);
    setDevice('');
    setFloor('');
    setSchedule({});
  };
  const saveMember = () => {
    const from = kst(start),
      until = kst(end);
    if (
      !from ||
      !until ||
      from >= until ||
      (!editing && (!!user.trim() === !!phone.trim() || (!!user.trim() && !isUuid(user.trim())))) ||
      (!editing && phone && !name.trim())
    ) {
      setMessage('회원 또는 전화번호와 이름, 권한 기간을 확인해 주세요.');
      return;
    }
    if (editing)
      void execute(
        () =>
          updateAccessMember(runtime.client, editing.id, {
            command_id: crypto.randomUUID(),
            expected_revision: editing.revision,
            valid_from: from,
            valid_until: until,
          }),
        '기간을 변경했습니다.',
      );
    else
      void execute(async () => {
        const key = JSON.stringify([from, until, user, phone, name]);
        if (inviteAttempt.current?.key !== key)
          inviteAttempt.current = { key, commandId: crypto.randomUUID(), id: crypto.randomUUID() };
        const result = await inviteGroupMember(runtime.client, group.id, {
          command_id: inviteAttempt.current.commandId,
          id: inviteAttempt.current.id,
          valid_from: from,
          valid_until: until,
          ...(user ? { user_id: user } : { phone: phone.trim(), name: name.trim() }),
        });
        inviteAttempt.current = null;
        return result;
      }, '초대를 저장했습니다.');
  };
  if (group.origin !== 'CORE') return null;
  return (
    <div className="space-y-6">
      <section aria-label="규칙 편집" className="space-y-2">
        <Button
          variant="outline"
          onClick={() => {
            setShowPicker(!showPicker);
          }}
        >
          기기 목록에서 선택
        </Button>
        {showPicker ? (
          <ClueDevicePicker
            runtime={runtime}
            generation={generation}
            onSelect={(id) => {
              setDevice(id);
              setShowPicker(false);
            }}
          />
        ) : null}
        <h3>규칙 편집</h3>
        <ul>
          {draft.map((r, i) => (
            <li key={r.id}>
              {r.device_id} · {r.floor ?? '문'}{' '}
              <Button
                variant="outline"
                onClick={() => {
                  setDraft(draft.filter((_, index) => index !== i));
                }}
              >
                규칙 삭제
              </Button>
            </li>
          ))}
        </ul>
        <label>
          기기 UUID
          <Input
            value={device}
            onChange={(e) => {
              setDevice(e.target.value);
            }}
          />
        </label>
        <label>
          엘리베이터 층
          <Input
            value={floor}
            onChange={(e) => {
              setFloor(e.target.value);
            }}
          />
        </label>
        {days.map(([day, label]) => (
          <label key={day}>
            {label} 시간표
            <Input
              value={schedule[day] ?? ''}
              placeholder="00:00-24:00"
              onChange={(e) => {
                setSchedule({ ...schedule, [day]: e.target.value });
              }}
            />
          </label>
        ))}
        <p>시간 구간은 쉼표로 구분합니다. 비워 둔 요일은 출입할 수 없습니다.</p>
        <Button onClick={addRule}>규칙 추가</Button>
        <Button
          disabled={busy}
          onClick={() =>
            void execute(
              () =>
                replaceAccessGroupRules(runtime.client, group.id, {
                  command_id: crypto.randomUUID(),
                  expected_revision: group.revision,
                  rules: draft,
                }),
              '규칙을 저장했습니다.',
            )
          }
        >
          규칙 저장
        </Button>
      </section>
      <section aria-label="멤버 관리" className="space-y-2">
        <h3>멤버 관리</h3>
        {editing ? (
          <p>{editing.name ?? editing.user_id ?? '비회원'} 기간 수정</p>
        ) : (
          <>
            <label>
              회원 UUID
              <Input
                value={user}
                onChange={(e) => {
                  setUser(e.target.value);
                }}
              />
            </label>
            <label>
              초대받을 사람 이름
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </label>
            <label>
              초대받을 전화번호
              <Input
                type="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                }}
              />
            </label>
          </>
        )}
        <label>
          권한 시작 KST
          <Input
            type="datetime-local"
            value={start}
            onChange={(e) => {
              setStart(e.target.value);
            }}
          />
        </label>
        <label>
          권한 종료 KST
          <Input
            type="datetime-local"
            value={end}
            onChange={(e) => {
              setEnd(e.target.value);
            }}
          />
        </label>
        <Button disabled={busy} onClick={saveMember}>
          {editing ? '기간 저장' : '멤버 초대'}
        </Button>
        {editing ? (
          <Button
            variant="outline"
            onClick={() => {
              setEditing(undefined);
            }}
          >
            수정 취소
          </Button>
        ) : null}
        <ul>
          {members.map((m) => (
            <li key={m.id}>
              {m.name ?? m.user_id ?? '비회원'} · {m.phone_masked ?? '—'} · {m.source_kind}
              {m.source_kind === 'ADMIN' && !m.revoked_at ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditing(m);
                      setStart('');
                      setEnd('');
                    }}
                  >
                    기간 수정
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setConfirm(m);
                    }}
                  >
                    회수
                  </Button>
                  {m.pass_id ? (
                    <Button
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        void execute(
                          () =>
                            resendGroupMember(runtime.client, group.id, m.id, {
                              command_id: crypto.randomUUID(),
                              expected_revision: m.revision,
                            }),
                          '재발송을 요청했습니다.',
                        )
                      }
                    >
                      재발송
                    </Button>
                  ) : null}
                </>
              ) : null}
              {confirm?.id === m.id ? (
                <Button
                  disabled={busy}
                  onClick={() =>
                    void execute(
                      () =>
                        revokeAccessMember(runtime.client, m.id, {
                          command_id: crypto.randomUUID(),
                          expected_revision: m.revision,
                        }),
                      '멤버를 회수했습니다.',
                    )
                  }
                >
                  회수 확인
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
      {message ? <p role="status">{message}</p> : null}
    </div>
  );
}
